import React, { useState, useEffect } from 'react';
import axios from 'axios';

const WebsiteScraping = () => {
  const [settings, setSettings] = useState({
    website_url: '',
    sitemap_url: '',
    is_enabled: true
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pages, setPages] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchPages();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('https://chatpanel.my.id/api/website-scraping/settings');
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        if (response.data.data.last_scraped) {
          setStats({
            lastScraped: new Date(response.data.data.last_scraped).toLocaleString(),
            totalPages: response.data.data.total_pages
          });
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await axios.get('https://chatpanel.my.id/api/website-scraping/pages');
      if (response.data.success) {
        setPages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('https://chatpanel.my.id/api/website-scraping/settings', settings);
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleScrape = async () => {
    if (!settings.website_url) {
      setMessage({ type: 'error', text: 'Please configure website URL first' });
      return;
    }

    setScraping(true);
    setMessage({ type: 'info', text: 'Scraping in progress... This may take a few minutes.' });

    try {
      const response = await axios.post('https://chatpanel.my.id/api/website-scraping/scrape');
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        fetchSettings();
        fetchPages();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Scraping failed' });
    } finally {
      setScraping(false);
    }
  };

  return (
    <div className="fade-in-up" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '700',
          marginBottom: '8px',
          color: '#1a1a1a'
        }}>
          Website Content Scraping
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#666666',
          margin: 0
        }}>
          Configure your website to be scraped for AI knowledge base. The AI will prioritize this content when answering questions.
        </p>
      </div>

      {/* Message Notification */}
      {message.text && (
        <div className="glass-card" style={{
          marginBottom: '24px',
          padding: '16px 20px',
          background: message.type === 'success' 
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
            : message.type === 'error'
            ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
          border: `2px solid ${
            message.type === 'success' ? 'rgba(16, 185, 129, 0.3)' 
            : message.type === 'error' ? 'rgba(220, 38, 38, 0.3)' 
            : 'rgba(59, 130, 246, 0.3)'
          }`,
          animation: 'slideDown 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>
              {message.type === 'success' ? '✅' : message.type === 'error' ? '❌' : 'ℹ️'}
            </span>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              color: message.type === 'success' ? '#059669' 
                : message.type === 'error' ? '#dc2626' 
                : '#3b82f6'
            }}>
              {message.text}
            </p>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
        gap: '24px', 
        marginBottom: '32px' 
      }}>
        {/* Settings Form */}
        <div className="glass-card">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid rgba(112, 0, 0, 0.1)'
          }}>
            <span style={{ fontSize: '28px' }}>⚙️</span>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              margin: 0,
              color: '#1a1a1a'
            }}>
              Website Settings
            </h2>
          </div>
          
          <form onSubmit={handleSaveSettings}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                fontSize: '14px',
                color: '#1a1a1a'
              }}>
                🌐 Website URL *
              </label>
              <input
                type="url"
                name="website_url"
                value={settings.website_url}
                onChange={handleInputChange}
                placeholder="https://example.com"
                required
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  border: '2px solid rgba(112, 0, 0, 0.2)', 
                  borderRadius: '10px', 
                  fontSize: '14px',
                  background: 'rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#700000';
                  e.target.style.boxShadow = '0 0 0 3px rgba(112, 0, 0, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(112, 0, 0, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Main website URL to scrape
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                fontSize: '14px',
                color: '#1a1a1a'
              }}>
                🗺️ Sitemap URL (Optional)
              </label>
              <input
                type="url"
                name="sitemap_url"
                value={settings.sitemap_url}
                onChange={handleInputChange}
                placeholder="https://example.com/sitemap.xml"
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  border: '2px solid rgba(112, 0, 0, 0.2)', 
                  borderRadius: '10px', 
                  fontSize: '14px',
                  background: 'rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#700000';
                  e.target.style.boxShadow = '0 0 0 3px rgba(112, 0, 0, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(112, 0, 0, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Leave empty to scrape only the main URL
              </small>
            </div>

            <div style={{ 
              marginBottom: '24px',
              padding: '16px',
              background: 'rgba(112, 0, 0, 0.05)',
              borderRadius: '10px',
              border: '2px solid rgba(112, 0, 0, 0.1)'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  name="is_enabled"
                  checked={settings.is_enabled}
                  onChange={handleInputChange}
                  style={{ 
                    marginRight: '12px', 
                    width: '20px', 
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#700000'
                  }}
                />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a1a' }}>
                  ✨ Enable website content for AI
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading 
                  ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                  : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(220, 38, 38, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                }
              }}
            >
              {loading ? (
                <>
                  <span style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid white', 
                    borderTop: '2px solid transparent', 
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></span>
                  Saving...
                </>
              ) : (
                <>💾 Save Settings</>
              )}
            </button>
          </form>
        </div>

        {/* Scraping Actions */}
        <div className="glass-card">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid rgba(112, 0, 0, 0.1)'
          }}>
            <span style={{ fontSize: '28px' }}>🚀</span>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              margin: 0,
              color: '#1a1a1a'
            }}>
              Scraping Actions
            </h2>
          </div>
          
          {/* Stats */}
          {stats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                borderRadius: '12px',
                border: '2px solid rgba(139, 92, 246, 0.2)'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>🕐</div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                  LAST SCRAPED
                </div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#7c3aed' }}>
                  {stats.lastScraped}
                </div>
              </div>

              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                borderRadius: '12px',
                border: '2px solid rgba(16, 185, 129, 0.2)'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>📄</div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                  TOTAL PAGES
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                  {stats.totalPages}
                </div>
              </div>
            </div>
          )}

          {/* Scrape Button */}
          <button
            onClick={handleScrape}
            disabled={scraping || !settings.website_url}
            style={{
              width: '100%',
              padding: '16px',
              background: (scraping || !settings.website_url)
                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: (scraping || !settings.website_url) ? 'not-allowed' : 'pointer',
              boxShadow: (scraping || !settings.website_url) 
                ? 'none' 
                : '0 4px 12px rgba(5, 150, 105, 0.3)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '20px'
            }}
            onMouseEnter={(e) => {
              if (!scraping && settings.website_url) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(5, 150, 105, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!scraping && settings.website_url) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.3)';
              }
            }}
          >
            {scraping ? (
              <>
                <span style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '2px solid white', 
                  borderTop: '2px solid transparent', 
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite'
                }}></span>
                Scraping... Please wait
              </>
            ) : (
              <>🚀 Start Scraping Now</>
            )}
          </button>

          {/* Warning Note */}
          <div style={{ 
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
            borderRadius: '10px',
            border: '2px solid rgba(245, 158, 11, 0.3)',
            fontSize: '13px'
          }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <div>
                <div style={{ fontWeight: '700', color: '#d97706', marginBottom: '4px' }}>
                  Important Note
                </div>
                <div style={{ color: '#666', lineHeight: '1.5' }}>
                  Scraping may take several minutes depending on the number of pages. 
                  The process runs in the background.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scraped Pages Table */}
      {pages.length > 0 && (
        <div className="glass-card">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '24px' 
          }}>
            <span style={{ fontSize: '28px' }}>📚</span>
            <div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: '700',
                margin: 0,
                color: '#1a1a1a'
              }}>
                Scraped Pages
              </h2>
              <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                {pages.length} page{pages.length !== 1 ? 's' : ''} indexed
              </p>
            </div>
          </div>
          
          <div style={{ 
            overflowX: 'auto',
            background: 'rgba(112, 0, 0, 0.02)',
            borderRadius: '12px',
            border: '1px solid rgba(112, 0, 0, 0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ 
                  background: 'linear-gradient(135deg, rgba(112, 0, 0, 0.05) 0%, rgba(163, 0, 33, 0.05) 100%)'
                }}>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    borderBottom: '2px solid rgba(112, 0, 0, 0.1)',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    📄 Page Title
                  </th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    borderBottom: '2px solid rgba(112, 0, 0, 0.1)',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    🔗 URL
                  </th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    borderBottom: '2px solid rgba(112, 0, 0, 0.1)',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    🕐 Scraped At
                  </th>
                </tr>
              </thead>
              <tbody>
                {pages.map((page, index) => (
                  <tr 
                    key={page.id} 
                    style={{ 
                      borderBottom: index < pages.length - 1 ? '1px solid rgba(112, 0, 0, 0.1)' : 'none',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(112, 0, 0, 0.03)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td style={{ 
                      padding: '14px 16px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#1a1a1a'
                    }}>
                      {page.page_title}
                    </td>
                    <td style={{ 
                      padding: '14px 16px', 
                      fontSize: '13px', 
                      color: '#666',
                      maxWidth: '400px'
                    }}>
                      <a 
                        href={page.page_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ 
                          color: '#700000',
                          textDecoration: 'none',
                          fontWeight: '500',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#a30021'}
                        onMouseLeave={(e) => e.target.style.color = '#700000'}
                      >
                        {page.page_url.length > 50 ? page.page_url.substring(0, 50) + '...' : page.page_url}
                      </a>
                    </td>
                    <td style={{ 
                      padding: '14px 16px', 
                      fontSize: '13px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {new Date(page.scraped_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .fade-in-up {
          animation: fadeInUp 0.6s ease;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default WebsiteScraping;
